import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect } from 'react';
import {
  orderFormSchema,
  type OrderFormData,
} from '../validation/order-form.schemas';
import type { WasteCollectionFormDTO, WasteCollectionFormReportDTO } from '../api/waste-collection.api';
import { applyServerFieldErrors } from '@/shared/utils';
import { sanitizeAddress } from '@/shared/utils/sanitize-address';

/**
 * Custom hook for order form dialog using react-hook-form + Zod
 *
 * Features:
 * - Initializes form with existing order data
 * - Client-side validation with Zod
 * - Address toggle logic (auto-populate from school address)
 * - Handles read-only fields (date, photos)
 * - Backend error mapping to form fields
 *
 * Usage:
 * ```tsx
 * const { form, onSubmit, isSubmitting } = useOrderFormDialog({
 *   orderData,
 *   onSave: async (data) => { await updateOrder(data); },
 *   schoolAddress: orderData.school?.adresa,
 * });
 * <Form {...form}>
 *   <form onSubmit={form.handleSubmit(onSubmit)}>
 *     ...
 *   </form>
 * </Form>
 * ```
 */

interface UseOrderFormDialogProps {
  orderData: WasteCollectionFormReportDTO;
  onSave: (data: Partial<WasteCollectionFormDTO>) => Promise<void>;
  schoolAddress?: any; // SchoolAddressDto type
}

export function useOrderFormDialog({
  orderData,
  onSave,
  schoolAddress,
}: UseOrderFormDialogProps) {
  // Initialize form with Zod validation
  const form = useForm<OrderFormData>({
    resolver: zodResolver(orderFormSchema),
    defaultValues: {
      id: orderData.id,
      schoolId: orderData.school?.id,
      contactPersonName: orderData.contactPersonName || '',
      contactPersonEmail: orderData.contactPersonEmail || '',
      contactPersonPhone: orderData.contactPersonPhone || '',
      deeeKg: orderData.deeeKg || 0,
      dbaKg: orderData.dbaKg || 0,
      becuriSiNeoaneKg: orderData.becuriSiNeoaneKg || 0,
      dataOrientativaColectare: orderData.dataOrientativaColectare
        ? new Date(orderData.dataOrientativaColectare)
        : undefined,
      pickupAddress: sanitizeAddress(orderData.pickupAddress) || {
        judet: '',
        oras: '',
        comuna: '',
        sat: '',
        strada: '',
        numar: '',
        bloc: '',
        scara: '',
        etaj: '',
        apartament: '',
        codPostal: '',
      },
      pickupAddressSameWithSchoolAddress: orderData.pickupAddressSameWithSchoolAddress || false,
      photos: orderData.photos || [],
      consent: true,
    },
  });

  // Auto-populate address when toggle is enabled (with reactive validation)
  useEffect(() => {
    const subscription = form.watch((value, { name }) => {
      if (name === 'pickupAddressSameWithSchoolAddress' && value.pickupAddressSameWithSchoolAddress) {
        if (schoolAddress) {
          // `sanitizeAddress`: adresa scolii vine cu `null` pe campurile necompletate, iar
          // `addressSchema` il respinge — bifa lasa dialogul nevalid, fara nicio cerere trimisa
          // (capcana #16). Fara `id`: e id-ul randului de adresa al SCOLII, nu al comenzii.
          const { id: _idScoala, ...adresaScolii } = sanitizeAddress(schoolAddress as Record<string, unknown>);
          form.setValue('pickupAddress', adresaScolii as OrderFormData['pickupAddress'], { shouldValidate: true });
        }
      } else if (name === 'pickupAddressSameWithSchoolAddress' && !value.pickupAddressSameWithSchoolAddress) {
        // Clear address when unchecked
        form.setValue('pickupAddress', {
          judet: '',
          oras: '',
          strada: '',
          numar: '',
          bloc: '',
          scara: '',
          etaj: '',
          apartament: '',
          codPostal: '',
        }, { shouldValidate: true });
      }
    });
    return () => subscription.unsubscribe();
  }, [form, schoolAddress]);

  // Form submission handler
  const onSubmit = async (data: OrderFormData) => {
    try {
      await onSave(data as Partial<WasteCollectionFormDTO>);
    } catch (error: unknown) {
      console.error('Order save error:', error);

      // Map backend errors to form fields
      applyServerFieldErrors(error, form.setError);

      // Re-throw to let parent component handle
      throw error;
    }
  };

  return {
    form,
    onSubmit,
  };
}
